import React, { useCallback, useState } from 'react';
import SeloDeComentarios from '../../../components/comentarios/SeloDeComentarios';

import {
  FiltrosCompras,
  FiltrosDaLista,
  FiltrosDeRecorteDePedido,
  PedidosComOrigem,
  fetchPedidosComOrigem,
} from '../../../api/comprasDashboardApi';
import DetalheDoPedidoModal from './DetalheDoPedidoModal';
import { formatarDataPura } from '../formatarData';
import { SITUACOES_DE_PEDIDO, rotuloDaSituacaoDePedido } from '../situacoes';
import ModalDeDetalhe, { SUFIXO_FILTRADO, useDetalhePaginado } from './ModalDeDetalhe';

/**
 * Os pedidos que compõem um número da aba Pedidos — o clique num KPI, numa coluna do funil ou
 * numa linha de qualquer card de agrupamento (at-121).
 *
 * É o irmão, do lado do pedido, do `RequisicoesDoRecorteModal`: a mesma ideia de "clique em
 * qualquer número para ver o que ele conta", sobre a lista de pedidos (`pedidos/lista`).
 *
 * 🔴 Os filtros que definem a lista chegam PRONTOS em `aberto.filtrosExtras`, montados por quem
 * renderizou o número — o comprador da linha, o status do card, a situação da coluna. Montá-los
 * aqui seria uma segunda definição do mesmo recorte, e a lista deixaria de somar o número clicado.
 * Os filtros do painel (`filtros`) entram por baixo, pelo mesmo motivo.
 *
 * Molde: `PedidosDoFornecedorModal` (at-110), com as mesmas quatro colunas.
 */

const POR_PAGINA = 25;

export interface PedidosDoRecorteAberto {
  titulo: string;
  /** O que a lista reúne, em uma frase — aparece ao lado do total. */
  legenda?: string;
  filtrosExtras?: FiltrosDeRecorteDePedido;
}

interface Props {
  aberto: PedidosDoRecorteAberto;
  filtros: FiltrosCompras;
  onFechar: () => void;
}

/**
 * As quatro colunas do molde da at-110 + o comprador (at-124, Victor, 08/10/2026): ele saiu dos
 * cards de oficina e placa, onde um nome por grupo de 242 pedidos se lia como "foi ele quem fez
 * tudo". Aqui é exato: um comprador por pedido.
 *
 * at-129: a "Situação" (gerencial) entrou quando as colunas Abertos e Cancelados saíram dos cards
 * (decisão do Victor, 22/10/2026) — a situação passou a ser lida pedido a pedido, aqui.
 */
const COLUNAS = ['Pedido', 'Emissão', 'Situação', 'Valor', 'Placa', 'Comprador', 'Previsão de entrega'];

const MOEDA = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** 🔴 A emissão do pedido é data PURA (meia-noite UTC): `new Date` a mostraria um dia antes. */
function formatarData(iso: string | null): string {
  return formatarDataPura(iso);
}

export default function PedidosDoRecorteModal({ aberto, filtros, onFechar }: Props) {
  const carregar = useCallback(
    (pagina: number, daLista: FiltrosDaLista) =>
      fetchPedidosComOrigem({
        ...filtros,
        ...aberto.filtrosExtras,
        // at-90: os filtros da lista vão com prefixo próprio e o servidor os põe em AND.
        ...daLista,
        pagina,
        limite: POR_PAGINA,
      }) as Promise<PedidosComOrigem>,
    [aberto.filtrosExtras, filtros],
  );

  const { dados, carregando, erro, pagina, setPagina, filtrosDaLista, aplicarFiltrosDaLista, filtrado } =
    useDetalhePaginado(carregar);
  const [pedidoAberto, setPedidoAberto] = useState<{ id: string; numero: string | null } | null>(
    null,
  );

  const total = dados?.total ?? 0;
  const pedidos = dados?.pedidos ?? [];
  const somaDaPagina = pedidos.reduce((s, p) => s + p.total, 0);
  const contagem = `${total.toLocaleString('pt-BR')} ${total === 1 ? 'pedido' : 'pedidos'}${filtrado ? SUFIXO_FILTRADO : ''}`;

  return (
    <>
      <ModalDeDetalhe
        titulo={aberto.titulo}
        legenda={
          carregando
            ? 'Carregando...'
            : erro
              ? 'Não foi possível carregar'
              : aberto.legenda
                ? `${contagem} · ${aberto.legenda}`
                : contagem
        }
        carregando={carregando}
        erro={erro}
        erroTexto={
          'Não foi possível carregar estes pedidos. Isto não significa que não haja pedidos no ' +
          'recorte — tente de novo e, se persistir, acione a TI.'
        }
        vazio={pedidos.length === 0}
        vazioTexto={
          filtrado
            ? 'Nenhum pedido deste recorte com os filtros da lista. Ajuste ou limpe os filtros.'
            : 'Nenhum pedido neste recorte, no período e nos centros de custo escolhidos.'
        }
        filtroDaLista={{
          campos: ['busca', 'situacoes', 'placa', 'oficina', 'comprador', 'solicitante', 'fornecedor', 'periodo'],
          situacoes: SITUACOES_DE_PEDIDO,
          rotuloDaBusca: 'número, placa, fornecedor, comprador ou oficina',
          rotuloDoPeriodo: 'Emissão do pedido',
          aplicados: filtrosDaLista,
          onAplicar: aplicarFiltrosDaLista,
        }}
        colunas={COLUNAS}
        classeTabela="dcr-tabela-pedidos-recorte"
        paginacao={{
          pagina,
          ultimaPagina: Math.max(1, Math.ceil(total / POR_PAGINA)),
          onMudar: setPagina,
        }}
        rodape={<span>Soma desta página: {MOEDA.format(somaDaPagina)}</span>}
        travarFechamento={Boolean(pedidoAberto)}
        onFechar={onFechar}
      >
        {pedidos.map((p) => (
          <div key={p.id} className="dcr-linha">
            <div className="dcr-celula" title={p.numero ?? ''}>
              <button
                type="button"
                className="dcr-celula-link"
                onClick={() => setPedidoAberto({ id: p.id, numero: p.numero })}
              >
                {p.numero ?? 'sem número'}
              </button>
              <SeloDeComentarios total={p.comentarios} />
            </div>
            <div className="dcr-celula">{formatarData(p.emissao)}</div>
            <div className="dcr-celula" title={rotuloDaSituacaoDePedido(p.situacaoGerencial)}>
              {rotuloDaSituacaoDePedido(p.situacaoGerencial)}
            </div>
            <div className="dcr-celula">{MOEDA.format(p.total)}</div>
            {/*
              A placa é HERDADA da requisição de origem (at-109), e o pedido pode não ter origem.
              A célula diz isso em vez de mostrar traço, que seria lido como "o dado sumiu".
            */}
            <div className="dcr-celula">
              {p.placa ??
                (p.requisicaoCodigo ? (
                  <span className="dcr-ausente">sem placa</span>
                ) : (
                  <span className="dcr-ausente">sem requisição</span>
                ))}
            </div>
            {/* O comprador também é herdado da requisição de origem — mesma regra da placa. */}
            <div className="dcr-celula" title={p.compradorNome ?? ''}>
              {p.compradorNome ??
                (p.requisicaoCodigo ? (
                  <span className="dcr-ausente">sem comprador</span>
                ) : (
                  <span className="dcr-ausente">sem requisição</span>
                ))}
            </div>
            <div className="dcr-celula">
              {p.previsaoDeEntrega ? (
                formatarData(p.previsaoDeEntrega)
              ) : (
                <span className="dcr-ausente">sem previsão</span>
              )}
            </div>
          </div>
        ))}
      </ModalDeDetalhe>

      {/* Irmão do casco, e não filho: o overlay dele precisa ficar por cima deste. */}
      {pedidoAberto && (
        <DetalheDoPedidoModal
          pedidoId={pedidoAberto.id}
          numero={pedidoAberto.numero}
          onFechar={() => setPedidoAberto(null)}
        />
      )}
    </>
  );
}
